import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { MobileControls } from '../components/MobileControls.js';
import { isMobileDevice } from '../utils/DeviceDetection.js';

export class FirstPersonControls {
  constructor(camera, domElement) {
    this.camera = camera;
    this.domElement = domElement;
    this.isMobile = isMobileDevice();

    // PointerLock controls for mouse look
    this.controls = new PointerLockControls(camera, domElement);

    // Movement state
    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;

    // Movement parameters
    this.velocity = new THREE.Vector3();
    this.direction = new THREE.Vector3();
    this.speed = 5.0; // Movement speed in units/second
    this.damping = 8.0; // Higher = more responsive, lower = more inertia

    // Collision detection callback (set externally)
    this.checkCollision = null;

    // Mobile controls
    this.mobileControls = null;
    if (this.isMobile) {
      this.mobileControls = new MobileControls(camera);
      this.mobileControls.enable();

      // Mobile camera rotation state
      this.mobileRotationX = 0; // Pitch
      this.mobileRotationY = 0; // Yaw
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Keyboard events (desktop only)
    if (!this.isMobile) {
      document.addEventListener('keydown', (e) => this.onKeyDown(e));
      document.addEventListener('keyup', (e) => this.onKeyUp(e));

      // Click to lock pointer
      this.domElement.addEventListener('click', () => {
        this.controls.lock();
      });

      // Handle lock/unlock events
      this.controls.addEventListener('lock', () => {
        console.log('Pointer locked');
      });

      this.controls.addEventListener('unlock', () => {
        console.log('Pointer unlocked');
      });
    }
  }

  onKeyDown(event) {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = true;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = true;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.moveLeft = true;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.moveRight = true;
        break;
    }
  }

  onKeyUp(event) {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = false;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.moveLeft = false;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.moveRight = false;
        break;
    }
  }

  update(delta) {
    // Mobile controls
    if (this.isMobile && this.mobileControls && this.mobileControls.enabled) {
      this.updateMobile(delta);
      return;
    }

    // Desktop controls (require pointer lock)
    if (!this.controls.isLocked) return;

    // Calculate movement direction
    this.direction.z = Number(this.moveForward) - Number(this.moveBackward);
    this.direction.x = Number(this.moveRight) - Number(this.moveLeft);
    this.direction.normalize();

    // Apply damping for smooth movement
    const targetVelocityX = this.direction.x * this.speed;
    const targetVelocityZ = this.direction.z * this.speed;

    this.velocity.x += (targetVelocityX - this.velocity.x) * this.damping * delta;
    this.velocity.z += (targetVelocityZ - this.velocity.z) * this.damping * delta;

    // Store old position for collision detection
    const oldPosition = this.camera.position.clone();

    // Move camera
    this.controls.moveRight(this.velocity.x * delta);
    this.controls.moveForward(this.velocity.z * delta);

    // Check for collisions if callback is set
    if (this.checkCollision && this.checkCollision(this.camera.position)) {
      // Collision detected, revert to old position
      this.camera.position.copy(oldPosition);

      // Try sliding along walls
      const slidePosition = oldPosition.clone();
      slidePosition.x = this.camera.position.x;
      if (!this.checkCollision(slidePosition)) {
        this.camera.position.x = slidePosition.x;
      }

      slidePosition.copy(oldPosition);
      slidePosition.z = this.camera.position.z;
      if (!this.checkCollision(slidePosition)) {
        this.camera.position.z = slidePosition.z;
      }
    }
  }

  updateMobile(delta) {
    const movement = this.mobileControls.getMovement();
    const rotationVelocity = this.mobileControls.getRotationVelocity();

    // Update camera rotation based on velocity
    const rotationSpeed = this.mobileControls.look.rotationSpeed;
    this.mobileRotationY -= rotationVelocity.y * rotationSpeed * delta; // Yaw (left/right)
    this.mobileRotationX -= rotationVelocity.x * rotationSpeed * delta; // Pitch (up/down)

    // Clamp pitch to avoid flipping
    this.mobileRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.mobileRotationX));

    // Apply rotation to camera
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.mobileRotationY;
    this.camera.rotation.x = this.mobileRotationX;

    // Calculate movement direction in camera space
    this.direction.z = movement.forward;
    this.direction.x = movement.right;

    // Apply damping for smooth movement
    const targetVelocityX = this.direction.x * this.speed;
    const targetVelocityZ = this.direction.z * this.speed;

    this.velocity.x += (targetVelocityX - this.velocity.x) * this.damping * delta;
    this.velocity.z += (targetVelocityZ - this.velocity.z) * this.damping * delta;

    // Store old position for collision detection
    const oldPosition = this.camera.position.clone();

    // Move camera in local space
    const forward = new THREE.Vector3(0, 0, -1);
    forward.applyQuaternion(this.camera.quaternion);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3(1, 0, 0);
    right.applyQuaternion(this.camera.quaternion);
    right.y = 0;
    right.normalize();

    this.camera.position.addScaledVector(right, this.velocity.x * delta);
    this.camera.position.addScaledVector(forward, this.velocity.z * delta);

    // Check for collisions if callback is set
    if (this.checkCollision && this.checkCollision(this.camera.position)) {
      // Collision detected, revert to old position
      this.camera.position.copy(oldPosition);

      // Try sliding along walls
      const slidePosition = oldPosition.clone();
      slidePosition.x = this.camera.position.x;
      if (!this.checkCollision(slidePosition)) {
        this.camera.position.x = slidePosition.x;
      }

      slidePosition.copy(oldPosition);
      slidePosition.z = this.camera.position.z;
      if (!this.checkCollision(slidePosition)) {
        this.camera.position.z = slidePosition.z;
      }
    }
  }

  getObject() {
    return this.controls.getObject();
  }

  lock() {
    this.controls.lock();
  }

  unlock() {
    this.controls.unlock();
  }

  get isLocked() {
    // On mobile, controls are always "locked" (active)
    if (this.isMobile) {
      return true;
    }
    return this.controls.isLocked;
  }

  dispose() {
    if (this.mobileControls) {
      this.mobileControls.dispose();
    }
  }
}
